// PEME-specific profile drawer with Physician Worklist pipeline checklist
// Matches Figma design exactly - see /Users/son.dinh/Downloads/figma-peme-status
import React, { useState, useEffect } from 'react';
import { Sheet, SheetContent, SheetTitle, SheetDescription } from 'components/ui/sheet';
import { X, Ship, Edit3, AlertTriangle, CheckCircle2, Circle } from 'lucide-react';
import { PIPELINE_STAGES } from '../constants/peme-stages';

const TABS = ['Overview', 'Documents', 'History'];

// Build pipeline checklist from stageHistory array
const buildChecklist = (stageHistory = [], currentStage, status) => {
  const historyMap = {};
  stageHistory.forEach((h) => { historyMap[h.stage] = h.status; });

  return PIPELINE_STAGES.map((stage) => {
    let stageStatus = 'pending';
    if (historyMap[stage] === 'done') stageStatus = 'done';
    else if (status === 'Fit to Work') stageStatus = 'done';
    return { stage, status: stageStatus };
  });
};

const PemeProfileDrawer = ({ open, onClose, crew, onEditProfile }) => {
  const [activeTab, setActiveTab] = useState('Overview');

  useEffect(() => {
    if (open) setActiveTab('Overview');
  }, [open]);

  if (!crew) return null;

  const nameParts = (crew.name || '').split(' ').filter(Boolean);
  const initials = nameParts.map((w) => w[0]).join('').slice(0, 2).toUpperCase() || '??';
  const checklist = buildChecklist(crew.stageHistory, crew.currentStage, crew.status);
  const pendingCount = checklist.filter((c) => c.status === 'pending').length;
  const hasPending = pendingCount > 0 && crew.status !== 'Fit to Work';

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="tw-w-full sm:tw-max-w-[480px] !tw-p-0 tw-overflow-y-auto tw-flex tw-flex-col" side="right">
        <SheetTitle className="tw-sr-only">PEME Profile</SheetTitle>
        <SheetDescription className="tw-sr-only">PEME profile for {crew.name}</SheetDescription>

        {/* Drawer Header - Figma: p-6 pb-0 */}
        <div className="tw-p-6 tw-pb-0">
          {/* Avatar row - Figma: flex justify-between items-start mb-6 */}
          <div className="tw-flex tw-justify-between tw-items-start tw-mb-6">
            {/* Avatar - Figma: w-16 h-16 rounded-full bg-[#4F46E5] text-white */}
            <div className="tw-w-16 tw-h-16 tw-rounded-full tw-bg-[#4F46E5] tw-text-white tw-flex tw-items-center tw-justify-center tw-text-xl tw-font-bold tw-shadow-sm">
              {initials}
            </div>
            {/* Close button - Figma: p-2 text-slate-400 rounded-full */}
            <button
              onClick={() => onClose(false)}
              className="tw-p-2 tw-text-slate-400 hover:tw-text-slate-600 hover:tw-bg-slate-100 tw-rounded-full tw-border-0 tw-bg-transparent tw-cursor-pointer tw-transition-colors"
            >
              <X className="tw-w-5 tw-h-5" />
            </button>
          </div>

          {/* Name section - Figma: wrapped in div, no extra spacing */}
          <div>
            <h2 className="tw-text-2xl tw-font-bold tw-text-slate-900 tw-tracking-tight tw-m-0">{crew.name}</h2>
            <p className="tw-text-slate-500 tw-mt-1 tw-mb-0 tw-text-sm">
              {crew.rank || 'No rank assigned'} • {crew.dept || 'Engine Dept'}
            </p>
          </div>

          {/* Action Buttons - Figma: flex space-x-3 mt-6 */}
          <div className="tw-flex tw-gap-3 tw-mt-6">
            <button className="tw-flex-1 tw-bg-[#4F46E5] hover:tw-bg-indigo-700 tw-text-white tw-py-2.5 tw-px-3 tw-rounded-xl tw-text-sm tw-font-semibold tw-flex tw-items-center tw-justify-center tw-transition-colors tw-shadow-sm tw-border-0 tw-cursor-pointer tw-whitespace-nowrap tw-min-w-0">
              <Ship className="tw-w-4 tw-h-4 tw-mr-2 tw-flex-shrink-0" />
              <span>Transfer Vessel</span>
            </button>
            <button
              onClick={() => onEditProfile && onEditProfile(crew)}
              className="tw-flex-1 tw-bg-white tw-border tw-border-slate-200 hover:tw-bg-slate-50 tw-text-slate-700 tw-py-2.5 tw-px-3 tw-rounded-xl tw-text-sm tw-font-semibold tw-flex tw-items-center tw-justify-center tw-transition-colors tw-shadow-sm tw-cursor-pointer tw-whitespace-nowrap tw-min-w-0"
            >
              <Edit3 className="tw-w-4 tw-h-4 tw-mr-2 tw-flex-shrink-0" />
              <span>Edit Profile</span>
            </button>
          </div>
        </div>

        {/* Tabs - Figma: flex border-b border-slate-200 mt-6 px-6 */}
        <div className="tw-flex tw-border-b tw-border-slate-200 tw-mt-5 tw-px-6">
          {TABS.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`tw-pt-1 tw-pb-3 tw-px-0 tw-mr-8 tw-text-sm tw-font-medium tw-border-0 tw-bg-transparent tw-cursor-pointer tw-transition-all tw-relative ${
                activeTab === tab
                  ? 'tw-text-[#4F46E5]'
                  : 'tw-text-slate-500 hover:tw-text-slate-700'
              }`}
            >
              {tab}
              {activeTab === tab && (
                <div className="tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-h-0.5 tw-bg-[#4F46E5] tw-rounded-t-full" />
              )}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="tw-flex-1 tw-overflow-y-auto tw-bg-[#F8FAFC] tw-px-6 tw-pt-6 tw-pb-6">
          {activeTab === 'Overview' && (
            <div className="tw-w-full">
              {/* Physician Worklist header */}
              <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
                <h3 className="tw-text-sm tw-font-bold tw-text-slate-900 tw-m-0">Physician Worklist</h3>
                {pendingCount > 0 && (
                  <span className="tw-inline-flex tw-items-center tw-text-[11px] tw-font-semibold tw-text-amber-700 tw-bg-amber-100 tw-border tw-border-amber-300 tw-px-2.5 tw-py-0.5 tw-rounded-full">
                    {pendingCount} pending
                  </span>
                )}
              </div>

              {/* Pending Clearance warning - rounded card */}
              {hasPending && (
                <div className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-3 tw-bg-rose-50 tw-border tw-border-rose-200 tw-rounded-xl tw-mb-3">
                  <AlertTriangle className="tw-w-4 tw-h-4 tw-text-rose-500 tw-flex-shrink-0" />
                  <span className="tw-text-xs tw-font-semibold tw-text-rose-600">Pending Clearance</span>
                </div>
              )}

              {/* Pipeline checklist - individual cards with spacing */}
              <div className="tw-space-y-3">
                {checklist.map(({ stage, status: st }) => {
                  const isDone = st === 'done';
                  return (
                    <div
                      key={stage}
                      className={`tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-3 tw-rounded-xl tw-border ${
                        isDone
                          ? 'tw-bg-emerald-50 tw-border-emerald-400'
                          : 'tw-bg-white tw-border-amber-400'
                      }`}
                    >
                      <div className="tw-flex tw-items-center tw-gap-3">
                        {isDone ? (
                          <CheckCircle2 className="tw-w-5 tw-h-5 tw-text-emerald-500" />
                        ) : (
                          <Circle className="tw-w-5 tw-h-5 tw-text-amber-400" strokeWidth={1.5} />
                        )}
                        <span className={`tw-text-sm tw-font-medium ${isDone ? 'tw-text-emerald-700' : 'tw-text-slate-700'}`}>
                          {stage}
                        </span>
                      </div>
                      <span className={`tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-wide tw-px-2.5 tw-py-1 tw-rounded-md tw-border ${
                        isDone
                          ? 'tw-bg-emerald-50 tw-text-emerald-600 tw-border-emerald-200'
                          : 'tw-bg-amber-50 tw-text-amber-600 tw-border-amber-200'
                      }`}>
                        {isDone ? 'Done' : 'Pending'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {activeTab === 'Documents' && (
            <div className="tw-text-center tw-py-8">
              <p className="tw-text-sm tw-text-slate-500 tw-m-0">No documents uploaded yet.</p>
            </div>
          )}

          {activeTab === 'History' && (
            <div className="tw-text-center tw-py-8">
              <p className="tw-text-sm tw-text-slate-500 tw-m-0">No recent activity history available.</p>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default PemeProfileDrawer;
